import { GoogleOAuthProvider } from '@react-oauth/google';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorInfo } from 'react';
import { ErrorBoundary } from "react-error-boundary";
import { Provider } from 'react-redux';

import AuthProvider from './providers/AuthProvider';
import AppRouterProvider from './providers/RouterProvider';
import { store } from './utils/store';

import { RiErrorWarningLine } from 'react-icons/ri';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      refetchOnWindowFocus: false,
      gcTime: 1000 * 30 * 60, // 30 minutes,
      staleTime: 1000 * 30 * 60, // 30 minutes,
    },
  },
})

/**
 * Used in case we want to inspect the query client in the browser console for debugging purposes.
 * You can access it by typing `window.__TANSTACK_QUERY_CLIENT__` in the console.
 */
// declare global {
//   interface Window {
//     __TANSTACK_QUERY_CLIENT__:
//     import('@tanstack/query-core')
//     .QueryClient
//   }
// }

// window.__TANSTACK_QUERY_CLIENT__ = queryClient

interface ProvidersProps extends React.PropsWithChildren { }

const Providers: React.FC<ProvidersProps> = ({ children }) => {
  return (
    <Provider store={store}>
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <GoogleOAuthProvider clientId={import.meta.env.VITE_GCP_OAUTH_CLIENT_ID}>
            {children}
          </GoogleOAuthProvider>
        </QueryClientProvider>
      </AuthProvider>
    </Provider>
  )
}


const errorFallbackRender = () => {
  return (
    <div className="mx-auto w-full h-full flex flex-col justify-center items-center p-4">
      <RiErrorWarningLine className="w-16 h-16 mb-4" />
      <p>We are experiencing a technical issue</p>
      <p>Please refresh or consult our Product team</p>
    </div>
  );
}

function logReactError(error: unknown, info: ErrorInfo) {
  console.group("React Runtime Error");
  console.error("Message:", (error as Error)?.message);
  console.error("Stack:", (error as Error)?.stack);
  console.log("Component stack:", info.componentStack);
  console.groupEnd();
}

function App() {
  return (
    <ErrorBoundary
      fallbackRender={errorFallbackRender}
      onError={logReactError}
    >
      <Providers>
        <AppRouterProvider />
      </Providers>
    </ErrorBoundary >
  )
}

export default App
